import { Environment } from "../environment";
import { urlSafeBase64 } from "../crypto";
import { IMGPROXY_URL } from "./imgproxy-config";
import { imgproxySign } from "./imgproxy-helpers";

export interface ImgproxyOptions {
  resizeType?: "fit" | "fill" | "fill-down" | "force" | "auto";
  width?: number;
  height?: number;

  minWidth?: number;
  minHeight?: number;
  gravity?: {
    type: string;
    xOffset?: number;
    yOffset?: number;
  };

  blur?: number;
  extension?: string;
  quality?: number;

  background?: string;
  backgroundAlpha?: number;

  thumbnailSecond?: number;

  responsive?: {
    targetWidth?: number;
    sizes: number[];
  };
  trim?: {
    treshold: number;
  };
  enlarge?: boolean;
  autoRotate?: boolean;
  extend?: boolean;
  extendAspectRatio?: boolean;
}

function getWidthAndRatio() {
  const anyGlobal: any = global;

  return {
    devicePixelRatio:
      "devicePixelRatio" in anyGlobal ? anyGlobal.devicePixelRatio : 2,
    screen: Environment.Dimensions.get("screen"),
  };
}

export const createImgproxyUrl = (
  origin: string,
  {
    resizeType,
    width,
    height,
    minWidth,
    minHeight,
    gravity,
    blur,
    quality,
    thumbnailSecond,
    extension,
    responsive,
    extend,
    extendAspectRatio,
    enlarge,
    autoRotate,
    background,
    backgroundAlpha,
    trim,
  }: ImgproxyOptions
) => {
  const options: string[] = [];

  if (resizeType) options.push(`rt:${resizeType}`);

  if (responsive) {
    // Reads screen metrics, so it stays inside the branch that wants them.
    const device = getWidthAndRatio();
    const pixelRatio = Math.max(Math.min(device.devicePixelRatio, 1), 1.5);

    const width = Math.min(
      responsive.targetWidth || device.screen.width,
      device.screen.width
    );
    const screenSize = pixelRatio * width;

    const { sizes } = responsive;

    if (sizes.length) {
      const size =
        sizes.find((size) => screenSize < size) || sizes[sizes.length - 1];

      options.push(`w:${size}`);
    }
  } else {
    if (width) options.push(`w:${width}`);
    if (height) options.push(`h:${height}`);

    if (minWidth) options.push(`mw:${minWidth}`);
    if (minHeight) options.push(`mh:${minHeight}`);
  }

  if (extend) options.push(`ex:1`);
  if (extendAspectRatio) options.push(`exar:1`);

  if (enlarge) options.push(`el:1`);

  if (autoRotate !== undefined) options.push(`ar:${autoRotate ? 1 : 0}`);

  if (background) options.push(`bg:${background}`);
  if (backgroundAlpha !== undefined) options.push(`bga:${backgroundAlpha}`);

  if (gravity)
    options.push(
      `g:${gravity.type}:${gravity.xOffset || 0}:${gravity.yOffset || 0}`
    );
  if (blur) options.push(`bl:${blur}`);
  if (quality) options.push(`q:${quality}`);

  if (trim) options.push(`t:${trim.treshold}`);

  if (thumbnailSecond !== undefined) options.push(`vts:${thumbnailSecond}`);

  const path = `/${options.join("/")}/${urlSafeBase64(origin)}${
    extension || ""
  }`;
  const signature = imgproxySign(path);

  return `${IMGPROXY_URL}/${signature}${path}`;
};
